import { InfoOutlined } from '@mui/icons-material'
import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  useTheme,
} from '@mui/material'
import { parse } from 'date-fns'
import { useEffect } from 'react'
import { useSelector } from 'react-redux'

import { Tooltip } from '@/components/primitives/Tooltip/Tooltip'
import { selectRecurrences } from '@/features/domain/routeplan'
import { selectUserConfiguration } from '@/features/domain/user'
import { formatDate } from '@/hooks'

import { useController } from '../../hooks/useController'
import { useControllerActions } from '../../hooks/useControllerActions'
import { useTexts } from '../../useTexts'

export function Body() {
  const {
    data: {
      mode,
      pairedOrderSteps,
      gotRecurrentOrders,
      selectedOrderSteps,
      allRecurrentOrders,
      cloneAlsoPairedStep,
      cloneEntireRecurrence,
      canDuplicateRecurrences,
    },
    updateData,
  } = useController()
  const { validateRecurrenceDuplication } = useControllerActions()

  const theme = useTheme()
  const texts = useTexts()

  const recurrences = useSelector(selectRecurrences)
  const userConfiguration = useSelector(selectUserConfiguration)
  const { today } = userConfiguration

  const todayDate = parse(today, 'yyyyMMdd', new Date())
  const toggleCloneAlsoPairedStep = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked
    updateData({ cloneAlsoPairedStep: checked })
  }

  const toggleDuplicateMode = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    updateData({ cloneEntireRecurrence: value === 'entire-recurrence' })
  }

  const gotSingleOrderStepType =
    new Set(Object.values(selectedOrderSteps.orderSteps).map(orderStep => orderStep.orderStepType))
      .size === 1

  const orderStepType = selectedOrderSteps.orderSteps[selectedOrderSteps.ids[0]].orderStepType
  const entireRecurrenceOptionDisabled =
    canDuplicateRecurrences || !gotSingleOrderStepType || !allRecurrentOrders
  const duplicateMode = cloneEntireRecurrence ? 'entire-recurrence' : 'this-order'

  const hasPastStartDateForReccurentOrders =
    gotRecurrentOrders &&
    Object.values(selectedOrderSteps.orderSteps).some(step => {
      const recurrenceId = step.order.recurrenceId
      return recurrenceId && recurrences[recurrenceId].startDate < today
    })

  const newStartDateForSingleReccurence =
    hasPastStartDateForReccurentOrders && mode === 'single'
      ? formatDate(todayDate, false, userConfiguration)
      : undefined

  const gotPairedOrderSteps = pairedOrderSteps.ids.length > 0

  useEffect(() => {
    validateRecurrenceDuplication()
  }, [validateRecurrenceDuplication])

  return (
    <>
      <RadioGroup value={duplicateMode} onChange={toggleDuplicateMode}>
        <FormControlLabel value="this-order" control={<Radio />} label={texts.thisOrder} />

        {gotPairedOrderSteps && (
          <FormControlLabel
            disabled={duplicateMode !== 'this-order'}
            checked={cloneAlsoPairedStep}
            onChange={toggleCloneAlsoPairedStep}
            control={<Checkbox />}
            label={
              <Stack direction="row" spacing={1} justifyContent="center" mt={1}>
                <span>{texts.duplicateAlso(gotSingleOrderStepType ? orderStepType : 'mixed')}</span>

                <Tooltip
                  title={texts.duplicateAlsoTooltip(
                    gotSingleOrderStepType ? orderStepType : 'mixed',
                  )}
                  placement="bottom"
                >
                  <InfoOutlined htmlColor={theme.palette.action.active} sx={{ opacity: 0.56 }} />
                </Tooltip>
              </Stack>
            }
            style={{ marginLeft: 20 }}
          />
        )}

        <FormControlLabel
          value="entire-recurrence"
          disabled={entireRecurrenceOptionDisabled}
          control={<Radio />}
          label={
            <Tooltip
              title={
                canDuplicateRecurrences
                  ? texts.startDateOutsidePlanningRangeWarning(mode)
                  : texts.entireRecurrenceOptionDisabledTooltip
              }
              placement="bottom"
              disabled={!entireRecurrenceOptionDisabled}
            >
              <>{texts.theEntireRecurrence}</>
            </Tooltip>
          }
        />
      </RadioGroup>
      {hasPastStartDateForReccurentOrders && (
        <Box paddingLeft={3}>
          <Alert severity="warning">
            {texts.reccurentOrdersDuplicateWarning(mode, newStartDateForSingleReccurence)}
          </Alert>
        </Box>
      )}
    </>
  )
}
